import { Button, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.shape?.small,
	},
	infoColor: {
		background: theme.palette.info.main,
		color: theme.palette.info.contrastText,
		'&:hover': {
			background: theme.palette.info[700],
		},
	},
	errorColor: {
		background: theme.palette.error.main,
		color: theme.palette.error.contrastText,
		'&:hover': {
			background: theme.palette.error[700],
		},
	},
	warningColor: {
		background: `linear-gradient(134.36deg, ${theme.palette.secondary[900]} 7.61%, ${theme.palette.secondary[800]} 110.28%)`,
		color: theme.palette.text.dark,
		'&:hover': {
			background: `linear-gradient(134.36deg, ${theme.palette.secondary[800]} 7.61%, ${theme.palette.secondary[900]} 110.28%)`,
		},
	},
	successColor: {
		background: theme.palette.success.main,
		color: theme.palette.success.contrastText,
		'&:hover': {
			background: theme.palette.success[700],
		},
	},
	success2Color: {
		background: theme.palette.success2.main,
		color: theme.palette.success.contrastText,
		'&:hover': {
			background: theme.palette.success2[700],
		},
	},
	rounded: {
		borderRadius: '50%',
	},
	disablehover: {
		'&:hover': {
			background: 'inherit',
		},
	},
}));

const ButtonKit = React.forwardRef((props, ref) => {
	const { isLoading, children, disabled, color, rounded, endLoading, startLoading, ...rest } = props;

	const classes = useStyles();

	const circularProgressColor =
		rest.variant === 'contained' ? 'inherit' : rest.variant === 'outlined' ? 'primary' : 'primary';

	const style = {
		margin: rest.endIcon || isLoading ? '0 2rem' : '',
	};

	return (
		<Button
			{...(rest.onClick && {
				onClick: rest?.onClick,
			})}
			disabled={isLoading || disabled}
			endIcon={
				isLoading && endLoading ? (
					<CircularProgress style={style} color={circularProgressColor} size={14} />
				) : (
					rest.endIcon
				)
			}
			startIcon={
				isLoading && startLoading ? (
					<CircularProgress style={style} color={circularProgressColor} size={14} />
				) : (
					rest.startIcon
				)
			}
			{...rest}
			classes={{
				root: clsx(classes.root, {
					[classes.infoColor]: rest.variant === 'contained' && !disabled && color === 'info',
					[classes.errorColor]: rest.variant === 'contained' && !disabled && color === 'error',
					[classes.warningColor]: rest.variant === 'contained' && !disabled && color === 'warning',
					[classes.successColor]: rest.variant === 'contained' && !disabled && color === 'success',
					[classes.success2Color]: rest.variant === 'contained' && !disabled && color === 'success2',
					[classes.rounded]: rounded,
					[classes.disablehover]: rest.disablehover,
				}),
			}}
			ref={ref}
		>
			{children}
		</Button>
	);
});

ButtonKit.displayName = 'Keepa ButtonKit';

ButtonKit.defaultProps = {
	isLoading: false,
	disabled: false,
	children: <></>,
	variant: 'contained',
	rounded: false,
	disablehover: false,
	size: 'medium',
	fullWidth: false,
	className: '',
};

ButtonKit.prototype = {
	disabled: PropTypes.bool.isRequired,
	isLoading: PropTypes.bool,
	children: PropTypes.element.isRequired,
	endIcon: PropTypes.element,
	startIcon: PropTypes.element,
	onClick: PropTypes.func,
	variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
	color: PropTypes.oneOf([
		'inherit' | 'primary' | 'secondary' | 'success' | 'success2' | 'error' | 'info' | 'warning',
	]),
	rounded: PropTypes.bool,
	disablehover: PropTypes.bool,
	size: PropTypes.oneOf(['small', 'medium', 'large']),
	fullWidth: PropTypes.bool,
	className: PropTypes.string,
};

export default ButtonKit;
